/**
 * Period close. MASTER_PROMPT section 6.25.
 *
 * A month is closed when every question about it has an answer, and the
 * checklist is that set of questions — each one traceable to something Phase 0
 * found in the June-2026 data.
 *
 * Closing is deliberately hard to do carelessly. Blocking items stop the close
 * outright; only a super admin can override, and only with a written reason.
 * Closing over an unexplained variance is exactly the habit this system exists
 * to break, so the screen never makes it the easy path.
 */

import { useCallback, useEffect, useState } from "react";
import { ApiError } from "../../lib/api";
import { ops } from "../../lib/api2";
import type { CloseCheck, CloseStatus, User } from "../../lib/types";
import { lastMonth } from "../../lib/format";

function statusTone(check: CloseCheck): string {
  if (check.passed) return "ok";
  return check.blocking ? "blocking" : "review";
}

function StatusMark({ check }: { check: CloseCheck }) {
  // A glyph as well as colour — meaning never rides on hue alone (7.5).
  const glyph = check.passed ? "✓" : check.blocking ? "✕" : "!";
  const label = check.passed ? "OK" : check.blocking ? "Blocking" : "Review";
  return (
    <span className={`checkmark checkmark--${statusTone(check)}`}>
      <span aria-hidden="true">{glyph}</span>
      <span className="checkmark__label">{label}</span>
    </span>
  );
}

export function PeriodClose({ user }: { user: User }) {
  const [month, setMonth] = useState(lastMonth);
  const [state, setState] = useState<CloseStatus | null>(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [banner, setBanner] = useState<{
    tone: "positive" | "negative" | "warning";
    text: string;
  } | null>(null);

  const load = useCallback(() => {
    setState(null);
    setBanner(null);
    ops
      .closeStatus(`${month}-01`)
      .then(setState)
      .catch((e: ApiError) => setBanner({ tone: "negative", text: e.message }));
  }, [month]);

  useEffect(load, [load]);

  const isAdmin = user.role === "SUPER_ADMIN";
  // The same three roles `services.close.close_period` accepts. A supervisor
  // was previously shown a Close button that could only ever 403.
  const canClose =
    isAdmin || user.role === "UNIT_HEAD" || user.role === "ACCOUNTS";
  const blockers = state?.checks.filter((c) => !c.passed && c.blocking) ?? [];
  const reviews = state?.checks.filter((c) => !c.passed && !c.blocking) ?? [];
  const passed = state?.checks.filter((c) => c.passed) ?? [];

  async function close(withOverride: boolean) {
    setBusy(true);
    setBanner(null);
    try {
      const result = await ops.closePeriod(
        `${month}-01`,
        withOverride ? reason : undefined
      );
      setState(result);
      setReason("");
      setBanner({
        tone: "positive",
        text: `${month} is closed. Nothing can be posted into it — corrections go into the open period as reversals.`,
      });
    } catch (err) {
      const e = err as ApiError;
      setBanner({
        tone: e.status === 422 ? "warning" : "negative",
        text: e.message,
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page">
      <header className="page__head">
        <div>
          <h2 className="page__title">Period close</h2>
          <p className="page__sub">
            A month is closed when every question about it has an answer
          </p>
        </div>
        <div className="page__controls">
          <label className="inputgroup">
            <span className="label">Period</span>
            <input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
            />
          </label>
          <button className="btn btn--ghost btn--auto" onClick={load}>
            Refresh
          </button>
        </div>
      </header>

      {banner && (
        <div className={`banner banner--${banner.tone}`} role="alert">
          {banner.text}
        </div>
      )}

      {!state ? (
        <div className="state">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="skeleton state__bar" />
          ))}
        </div>
      ) : (
        <>
          <section
            className={`card closehero closehero--${
              state.locked ? "locked" : state.can_close ? "ready" : "blocked"
            }`}
          >
            <div className="closehero__mark" aria-hidden="true">
              {state.locked ? "🔒" : state.can_close ? "✓" : "✕"}
            </div>
            <div>
              <h3 className="section-title">{state.summary}</h3>
              {/* If it was closed over a blocker, the reason travels with the
                  lock and belongs on the face of the screen — not buried in a
                  table somebody would have to go looking for. */}
              {state.locked && state.override_reason && (
                <p className="section-note">
                  <strong>Closed over a blocking item.</strong>{" "}
                  {state.override_reason}
                </p>
              )}
              {!state.locked && state.reopen_reason && (
                <p className="section-note">
                  <strong>Reopened.</strong> {state.reopen_reason}
                </p>
              )}
              <p className="section-note">
                {state.locked
                  ? "No movement can be posted into this month. A correction goes into the open period as a reversal — that is how an audit trail works."
                  : blockers.length
                    ? "Every blocking item below has to be resolved, or a super admin must close over it with a written reason."
                    : "Nothing is blocking. Anything under review is worth a look but will not stop the close."}
              </p>
            </div>
          </section>

          <div className="checklist">
            {[
              ["Blocking", blockers],
              ["Worth reviewing", reviews],
              ["Clear", passed],
            ].map(([title, rows]) => {
              const list = rows as CloseCheck[];
              if (!list.length) return null;
              return (
                <section key={title as string} className="card">
                  <h3 className="section-title">
                    {title as string}{" "}
                    <span className="pill">{list.length}</span>
                  </h3>
                  <ul className="checklist__list">
                    {list.map((check) => (
                      <li key={check.key} className="checklist__item">
                        <StatusMark check={check} />
                        <div>
                          <strong>{check.title}</strong>
                          <p className="section-note">{check.detail}</p>
                        </div>
                        {check.count > 0 && (
                          <span className="num mono checklist__count">
                            {check.count}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </section>
              );
            })}
          </div>

          {!state.locked && (
            <section className="card">
              {blockers.length > 0 ? (
                isAdmin ? (
                  <>
                    <h3 className="section-title">Close over the blockers</h3>
                    <p className="section-note">
                      Closing a month with {blockers.length} unresolved
                      item(s) needs a written reason, which is kept with the
                      lock. Closing over an unexplained variance is the habit
                      this system exists to break — be sure.
                    </p>
                    <div className="page__footer">
                      <label className="inputgroup inputgroup--grow">
                        <span className="label">Reason</span>
                        <input
                          value={reason}
                          onChange={(e) => setReason(e.target.value)}
                          placeholder="Auditor deadline; variance carried to July with the plant head's agreement"
                        />
                      </label>
                      <button
                        className="btn btn--auto"
                        onClick={() => close(true)}
                        disabled={busy || !reason.trim()}
                      >
                        {busy ? <span className="spinner" /> : null}
                        Close anyway
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <h3 className="section-title">Cannot close yet</h3>
                    <p className="section-note">
                      Resolve the blocking items above. Only a super admin can
                      close over them, and only with a written reason.
                    </p>
                  </>
                )
              ) : canClose ? (
                <>
                  <h3 className="section-title">Ready</h3>
                  <p className="section-note">
                    Once closed, no movement can be posted into {month}.
                  </p>
                  <div className="page__footer">
                    <span className="entrytotals__sub">
                      Closing is for the unit head or accounts.
                    </span>
                    <button
                      className="btn btn--auto"
                      onClick={() => close(false)}
                      disabled={busy}
                    >
                      {busy ? <span className="spinner" /> : null}
                      Close {month}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <h3 className="section-title">Ready to close</h3>
                  <p className="section-note">
                    Nothing is blocking {month}. Closing it is for the unit
                    head or accounts — this account can see the checklist but
                    not lock the period.
                  </p>
                </>
              )}
            </section>
          )}

          {state.locked && (
            <p className="page__note">
              Reopening a closed month is a super-admin action and always needs
              a written reason. It is available through the API; there is no
              button here on purpose.
            </p>
          )}
        </>
      )}
    </div>
  );
}
